import axios from 'axios';
import { cognitoApiEndPoints } from '../constants/ApiEndPoints';
import {
  getAuthItem,
  saveToken,
  clearAllTokens,
  isTokenExpired,
  constants,
} from '../utils/AuthUtils';

// Singleton state
let refreshPromise: Promise<void> | null = null;

/**
 * Refresh tokens by POSTing to the Cognito token endpoint
 * with grant_type=refresh_token. Saves both id_token and access_token.
 */
async function refreshTokens(): Promise<void> {
  const refreshToken = getAuthItem(constants.REFRESH_TOKEN);

  if (!refreshToken) {
    clearAllTokens();
    window.location.href = cognitoApiEndPoints.ssoLoginURL; //old code:loginURL
    return Promise.reject(new Error('No refresh token available'));
  }

  try {
    const params = new URLSearchParams();
    params.append('grant_type', 'refresh_token');
    params.append('refresh_token', refreshToken);
    params.append('client_id', import.meta.env.VITE_COGNITO_CLIENT_ID);

    const response = await axios.post(cognitoApiEndPoints.tokenURL, params, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });

    saveToken(response.data);
  } catch (error) {
    clearAllTokens();
    window.location.href = cognitoApiEndPoints.ssoLoginURL; //old code:loginURL
    throw error;
  }
}

/**
 * Ensure tokens are fresh. If expired, triggers a refresh.
 * Uses singleton pattern so concurrent callers share the same refresh promise.
 */
async function ensureValidTokens(): Promise<void> {
  const idToken = getAuthItem(constants.ID_TOKEN);
  const expiryTime = getAuthItem(constants.EXPIRY_TIME);

  if (idToken && !isTokenExpired(undefined, expiryTime)) {
    return;
  }

  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = refreshTokens().finally(() => {
    refreshPromise = null;
  });
  return refreshPromise;
}

/**
 * Get a valid ID token (used for backend API calls).
 */
export async function getValidIdToken(): Promise<string> {
  await ensureValidTokens();
  return getAuthItem(constants.ID_TOKEN);
}

/**
 * Get a valid access token (used for Cognito endpoints like /oauth2/userInfo).
 */
export async function getValidAccessToken(): Promise<string> {
  await ensureValidTokens();
  return getAuthItem(constants.ACCESS_TOKEN);
}

/** Reset internal state — exposed for testing only */
export function _resetForTesting(): void {
  refreshPromise = null;
}

